import React from "react";
import { Link, useNavigate } from "react-router-dom";

type RequestCardProps = {
  title: string;
  description: string;
  status: "ready" | "coming_soon";
  onClick?: () => void;
};

function RequestCard({ title, description, status, onClick }: RequestCardProps) {
  const isReady = status === "ready";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!isReady}
      className={`w-full rounded-[1.8rem] border p-5 text-left shadow-[0_12px_28px_rgba(124,90,78,0.10)] transition ${
        isReady
          ? "border-[#EADBC8] bg-white hover:-translate-y-0.5 hover:bg-[#FFFCFA]"
          : "cursor-not-allowed border-[#EEE3DB] bg-[#FAF6F2] opacity-75"
      }`}
    >
      <span
        className={`inline-flex rounded-full px-3 py-1 text-[11px] font-black ${
          isReady ? "bg-[#FFF3E9] text-[#A46D4C]" : "bg-white text-[#A1887D]"
        }`}
      >
        {isReady ? "すぐ使える" : "準備中"}
      </span>
      <h2 className="mt-4 text-lg font-black text-[#5D4D46]">{title}</h2>
      <p className="mt-2 text-sm leading-6 text-[#6B5851]">{description}</p>
      <p className="mt-4 text-sm font-bold text-[#A67C52]">{isReady ? "開く →" : "今後追加予定"}</p>
    </button>
  );
}

export default function RequestEntry() {
  const navigate = useNavigate();

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-md">
        <header className="mb-6 flex items-center justify-between">
          <Link to="/home" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ← HOME
          </Link>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">お願いを選ぶ</p>
          <span className="text-xs text-[#9C867D]">WizMate</span>
        </header>

        <section className="rounded-[2.4rem] border border-white/80 bg-white/90 p-5 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-7">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#A67C52]">Request Hub</p>
            <h1 className="mt-3 text-3xl font-black tracking-tight text-[#7C5A4E]">お願いしたいことを選ぶ</h1>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <RequestCard
              title="ニュース"
              description="隙間時間を、質の高いインプットに変えます。気になるテーマを短く整理して届けます。"
              status="ready"
              onClick={() => navigate("/request/news")}
            />
            <RequestCard
              title="レポート"
              description="テーマを深く整理して、考える材料をまとめます。調査テーマをもとに依頼できます。"
              status="ready"
              onClick={() => navigate("/request/report")}
            />
            <RequestCard
              title="領収書整理"
              description="写真を撮って、メイに丸投げするだけで整理できます。あとから見返しやすい形に整えます。"
              status="ready"
              onClick={() => navigate("/receipt", { state: { returnTo: "/request" } })}
            />
            <RequestCard
              title="スケジュール調整"
              description="予定確認や日程調整を手伝う予定です。"
              status="coming_soon"
            />
            <RequestCard
              title="移動・出張手配"
              description="移動ルートや出張手配をまとめて考えられるようにする予定です。"
              status="coming_soon"
            />
            <RequestCard title="買い物" description="必要なもの探しや比較を支援する予定です。" status="coming_soon" />
            <RequestCard title="代理応答" description="返答文のたたき台や一次対応を支援する予定です。" status="coming_soon" />
            <RequestCard title="名刺整理" description="名刺情報を取り込み、あとから探しやすくする予定です。" status="coming_soon" />
            <RequestCard title="手書きメモ整理" description="手書きメモを読み取り、整理しやすくする予定です。" status="coming_soon" />
          </div>
        </section>
      </div>
    </main>
  );
}
